import { useMemo, useState } from 'react';
import { filterReportTypes } from '../../utils';
import { ReportType, reportTypes } from '../../reportsTypes';
import {
  Input,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';

const noop = () => {
  // This function intentionally left blank for when the button is disabled. We cannot just disable the
  //  button because then we do not fire mouse events and thus do not get the tooltip
};

interface ReportTypeItemProps {
  reportType: ReportType;
  onReportClick: (value: ReportType) => void;
}

const ReportTypeItem = ({ reportType, onReportClick }: ReportTypeItemProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();

  const hasPermission = useMemo(
    () => permissions[reportType.type],
    [reportType, permissions],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center px-4 py-4 justify-between group/report-type-item mr-4 rounded-lg',
            { 'pointer-events-none opacity-50': reportType.disabled },
            {
              'opacity-50': !hasPermission,
            },
            {
              'hover:bg-secondary hover:cursor-pointer': hasPermission,
            },
          )}
          onClick={hasPermission ? () => onReportClick(reportType) : noop}
        >
          <div className="flex items-center">
            <reportType.icon
              className={cn('size-8 flex-shrink-0', {
                'group-hover/report-type-item:text-primary': hasPermission,
              })}
            />
            <div className="ml-4 space-y-1">
              <p className="text-sm font-medium leading-none">
                {reportType.label}
              </p>
              <p className="text-sm text-muted-foreground">
                {reportType.description}
              </p>
            </div>
          </div>
        </div>
      </TooltipTrigger>
      {!hasPermission && (
        <TooltipContent className="ml-4">
          {messages.reportsCreateReportChooseTypeMissingPermission.replace(
            '{label}',
            reportType.label,
          )}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

interface ReportTypesStepProps {
  setChosenReportType: (value: ReportType | null) => void;
}

export const ReportTypesStep = ({
  setChosenReportType,
}: ReportTypesStepProps) => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');

  const permissions = usePermissions();
  const { setInstantShape, setInstantShapeData } = useCreateReportContext();

  const reportTypesSorted = useMemo(
    () => [...reportTypes].sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const filteredReportTypes = filterReportTypes(
    reportTypesSorted,
    search,
    permissions,
  );

  const onReportClick = (reportType: ReportType) => {
    setChosenReportType(reportType);
    setInstantShapeData({});
    setInstantShape(false);
  };

  return (
    <div className="flex flex-col h-full max-h-[668px]">
      <div className="pt-4 pb-4 font-medium flex-shrink-0">
        {messages.reportsCreateReportChooseTypeAvailable}
      </div>
      <Input
        id="search-report-types"
        placeholder={messages.reportsCreateReportChooseTypeSearchPlaceholder}
        className="h-10 w-56 flex-shrink-0"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />
      <div className="flex-1 min-h-0 mt-4">
        <ScrollArea className="h-full p-0">
          <div className="pr-4">
            {filteredReportTypes.map((reportType, index) => (
              <ReportTypeItem
                key={index}
                reportType={reportType}
                onReportClick={onReportClick}
              />
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
